<script setup lang="ts">
import type { ResolvedMonospaceServiceAccount } from '#layers/data-model/public';
import type { DialogEmits } from '#layers/ui/public';
import { useEmitAsProps } from 'reka-ui';
import { computed } from 'vue';
import { useSystemEntityStore } from '#layers/runtime/public';

const { serviceAccount } = defineProps<{
	serviceAccount: ResolvedMonospaceServiceAccount;
}>();

const emit = defineEmits<DialogEmits>();
const forwarded = useEmitAsProps(emit);

const store = useSystemEntityStore();

const { data: apiKeys, loading } = store.MonospaceApiKey.query((q) =>
	q.many({ filter: { userId: serviceAccount.id } }),
);

const hasApiKeys = computed(() => apiKeys.value && apiKeys.value.length > 0);

// TODO: Refactor this once we have aggregate count in the API
const apiKeyCount = computed(() => apiKeys.value?.length ?? 0);
const workspaceCount = computed(() => serviceAccount.workspaces.length);
const hasWorkspaces = computed(() => serviceAccount.workspaces.length > 0);
</script>

<template>
	<DDialog
		v-bind="forwarded"
		:title="$t('common.delete_thing', { thing: serviceAccount.fullName ?? '' })"
		:confirm="{ color: 'error', label: $t('common.delete'), loading }"
	>
		<template #body>
			<div class="flex flex-col gap-4">
				<p class="text-muted">
					{{ $t('service_accounts.delete_confirm.description') }}
				</p>

				<DNotice
					v-if="loading"
					color="neutral"
					icon="lucide:loader-2"
					:description="$t('common.loading')"
				/>
				<div v-else class="flex flex-col gap-2">
					<DNotice
						v-if="hasApiKeys"
						color="warning"
						:title="$t('service_accounts.delete_confirm.api_keys_warning_title', { count: apiKeyCount })"
						:description="$t('service_accounts.delete_confirm.api_keys_warning_description', { count: apiKeyCount })"
					/>

					<DNotice
						v-if="hasWorkspaces"
						color="warning"
						:title="$t('service_accounts.delete_confirm.workspaces_warning_title', { count: workspaceCount })"
						:description="$t('service_accounts.delete_confirm.workspaces_warning_description', { count: workspaceCount })"
					/>
				</div>
			</div>
		</template>
	</DDialog>
</template>
